import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import ReactMarkdown from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import { geminiService } from '../services/geminiService';

interface Item{
  doc:{
    class_nm : string;
  }
}

interface PieChartProp{
  data : Item [];
  isPopup?: boolean;
  onClose?: () => void;
}

interface PieChartButtonProps {
  data: Item[];
}

export const PieChart:React.FC<PieChartProp> = ({data, isPopup = false, onClose}) => {
  ChartJS.register(ArcElement, Tooltip, Legend)
  
  const [currentLevel, setCurrentLevel] = useState(1);
  const [selectedLevel1, setSelectedLevel1] = useState<string | null>(null);
  const [selectedLevel2, setSelectedLevel2] = useState<string | null>(null);
  const [geminiAnalysis, setGeminiAnalysis] = useState<string>('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [cacheInfo, setCacheInfo] = useState<{ size: number; keys: string[] } | null>(null);
  const analysisRef = useRef<HTMLDivElement>(null);

  // Process hierarchical data
  const hierarchicalData = useMemo(() => {
    const level1Map = new Map<string, number>();
    const level2Map = new Map<string, number>();
    const level3Map = new Map<string, number>();
    
    console.log('Processing', data.length, 'items');
    
    data.forEach((element, index) => {
      const classNm = element.doc.class_nm.trim();
      if (index < 5) console.log('Sample class_nm:', classNm); // Log first 5 items
      
      const classA = classNm.split(">").map(s => s.trim()).filter(s => s.length > 0);
      
      if (classA[0]) {
        level1Map.set(classA[0], (level1Map.get(classA[0]) || 0) + 1);
      }
      
      if (classA[1]) {
        const level2Key = `${classA[0]} > ${classA[1]}`;
        level2Map.set(level2Key, (level2Map.get(level2Key) || 0) + 1);
      }
      
      if (classA[2]) {
        const level3Key = `${classA[0]} > ${classA[1]} > ${classA[2]}`;
        level3Map.set(level3Key, (level3Map.get(level3Key) || 0) + 1);
      }
    });
    
    console.log('Processed hierarchical data:');
    console.log('Level 1 entries:', level1Map.size);
    console.log('Level 2 entries:', level2Map.size);
    console.log('Level 3 entries:', level3Map.size);
    console.log('Sample level 2 keys:', Array.from(level2Map.keys()).slice(0, 5));
    
    return { level1Map, level2Map, level3Map };
  }, [data]);

  // Color palette
  const colors = [
    "#4b45e6", "#ff6384", "#4cc0c0", "#ff9e40", "#9a66ff",
    "#ffcd56", "#c9cbcf", "#36bb44", "#37a2eb", "#c6e203",
    "#e74c3c", "#3498db", "#2ecc71", "#f39c12", "#9b59b6",
    "#1abc9c", "#34495e", "#e67e22", "#95a5a6", "#f1c40f"
  ];

  // Generate chart data for each level
  const generateChartData = (dataMap: Map<string, number>) => {
    const sortedEntries = Array.from(dataMap.entries()).sort((a, b) => b[1] - a[1]);
    const labels = sortedEntries.map(([key]) => key);
    const values = sortedEntries.map(([, value]) => value);
    const backgroundColors = labels.map((_, index) => colors[index % colors.length]);
    
    return {
      labels,
      datasets: [{
        label: '# of Books',
        data: values,
        borderWidth: 1,
        backgroundColor: backgroundColors,
      }]
    };
  };

  // Get data for all levels
  const level1Data = hierarchicalData.level1Map;
  
  // Get level 2 data (filtered by selected level 1)
  const level2Data = useMemo(() => {
    const filtered = new Map<string, number>();
    if (selectedLevel1) {
      console.log('Filtering level 2 data for:', selectedLevel1);
      hierarchicalData.level2Map.forEach((count, key) => {
        if (key.startsWith(selectedLevel1)) {
          const level2Name = key.split(' > ')[1];
          if (level2Name) {
            filtered.set(level2Name, count);
            console.log('Added level 2:', level2Name, 'count:', count);
          }
        }
      });
      console.log('Level 2 filtered data size:', filtered.size);
    }
    return filtered;
  }, [hierarchicalData.level2Map, selectedLevel1]);
  
  // Get level 3 data (filtered by selected level 1 and 2)
  const level3Data = useMemo(() => {
    const filtered = new Map<string, number>();
    if (selectedLevel1 && selectedLevel2) {
      const searchKey = `${selectedLevel1} > ${selectedLevel2}`;
      console.log('Filtering level 3 data for:', searchKey);
      hierarchicalData.level3Map.forEach((count, key) => {
        if (key.startsWith(searchKey)) {
          const level3Name = key.split(' > ')[2];
          if (level3Name) {
            filtered.set(level3Name, count);
            console.log('Added level 3:', level3Name, 'count:', count);
          }
        }
      });
      console.log('Level 3 filtered data size:', filtered.size);
    }
    return filtered;
  }, [hierarchicalData.level3Map, selectedLevel1, selectedLevel2]);

  const getChartOptions = (title: string, onClick?: (index: number) => void) => ({
    responsive: true,
    plugins: {
      legend: {
        position: "bottom" as const,
        labels: {
          font: {
            size: 10
          },
          boxWidth: 12
        }
      },
      title: {
        display: true,
        text: title,
        font: {
          size: 14
        }
      }
    },
    maintainAspectRatio: false,
    onClick: onClick ? (_event: any, elements: any[]) => {
      if (elements.length > 0) {
        const clickedIndex = elements[0].index;
        onClick(clickedIndex);
      }
    } : undefined
  });

  const handleLevel1Click = (index: number) => {
    const labels = Array.from(level1Data.keys()).sort((a, b) => level1Data.get(b)! - level1Data.get(a)!);
    const selected = labels[index];
    console.log('Level 1 clicked:', selected, 'at index:', index);
    setSelectedLevel1(selected);
    setCurrentLevel(2);
  };

  const handleLevel2Click = (index: number) => {
    const labels = Array.from(level2Data.keys()).sort((a, b) => level2Data.get(b)! - level2Data.get(a)!);
    const selected = labels[index];
    console.log('Level 2 clicked:', selected, 'at index:', index);
    setSelectedLevel2(selected);
    setCurrentLevel(3);
  };

  const resetToLevel1 = () => {
    console.log('Resetting to level 1');
    setSelectedLevel1(null);
    setSelectedLevel2(null);
    setCurrentLevel(1);
    setGeminiAnalysis('');
    setAnalysisError(null);
    setIsStreaming(false);
  };

  const handleGeminiAnalysis = async () => {
    setIsAnalyzing(true);
    setIsStreaming(true);
    setAnalysisError(null);
    setGeminiAnalysis('');
    
    try {
      const chartData = {
        level1Data: hierarchicalData.level1Map,
        level2Data: level2Data,
        level3Data: level3Data,
        selectedLevel1,
        selectedLevel2,
        currentLevel
      };
      
      // Use streaming analysis
      await geminiService.analyzeChartDataStreaming(chartData, (chunk, isComplete) => {
        if (isComplete) {
          setIsStreaming(false);
          setIsAnalyzing(false);
          // Update cache info
          setCacheInfo(geminiService.getCacheStats());
        } else {
          setGeminiAnalysis(prev => prev + chunk);
          // Auto-scroll to bottom during streaming
          setTimeout(() => {
            if (analysisRef.current) {
              analysisRef.current.scrollTop = analysisRef.current.scrollHeight;
            }
          }, 0);
        }
      });
    } catch (error) {
      setAnalysisError(error instanceof Error ? error.message : 'AI 분석 중 오류가 발생했습니다.');
      setIsAnalyzing(false);
      setIsStreaming(false);
    }
  };

  const handleClearCache = () => {
    geminiService.clearCache();
    setCacheInfo(geminiService.getCacheStats());
  };

  // Update cache info on component mount
  useEffect(() => {
    setCacheInfo(geminiService.getCacheStats());
  }, []);

  // Progress Stepper Component
  const ProgressStepper = () => (
    <div className="chart-progress-stepper">
      <div className="progress-step">
        <div className={`progress-circle ${currentLevel === 1 ? 'active' : currentLevel > 1 ? 'completed' : 'pending'}`}>
          {currentLevel > 1 ? '✓' : '1'}
        </div>
        <div className="progress-label">주요 분류</div>
      </div>
      
      <div className={`progress-line ${currentLevel > 1 ? 'completed' : ''}`}></div>
      
      <div className="progress-step">
        <div className={`progress-circle ${currentLevel === 2 ? 'active' : currentLevel > 2 ? 'completed' : 'pending'}`}>
          {currentLevel > 2 ? '✓' : '2'}
        </div>
        <div className="progress-label">세부 분류</div>
      </div>
      
      <div className={`progress-line ${currentLevel > 2 ? 'completed' : ''}`}></div>
      
      <div className="progress-step">
        <div className={`progress-circle ${currentLevel === 3 ? 'active' : 'pending'}`}>
          3
        </div>
        <div className="progress-label">상세 분류</div>
      </div>
    </div>
  );

  // Ghost Chart Component
  const GhostChart = ({ level, title }: { level: number; title: string }) => (
    <div className="chart-ghost">
      <h3>{title}</h3>
      <div className="ghost-canvas">
        <div className="ghost-icon">📊</div>
        <div className="ghost-hint">
          {level === 2 ? "주요 분류를 클릭하면 나타납니다" : "세부 분류를 클릭하면 나타납니다"}
        </div>
      </div>
    </div>
  );

  // Debug: Log current state (only in development)
  if (process.env.NODE_ENV === 'development') {
    console.log('Current level:', currentLevel);
    console.log('Selected level 1:', selectedLevel1);
    console.log('Selected level 2:', selectedLevel2);
    console.log('Level 1 data size:', level1Data.size);
    console.log('Level 2 data size:', level2Data.size);
    console.log('Level 3 data size:', level3Data.size);
  }

  const chartContent = (
    <div className={isPopup ? "" : "chart-wrap"}>
      <ProgressStepper />
      <div className="chart-container">
        {/* Level 1 Chart */}
        <div className={`chart-level chart-level-1 ${currentLevel === 1 ? '' : 'shifted'}`}>
          <h3>주요 분류</h3>
          <div className={`chart-canvas ${currentLevel === 1 ? 'clickable' : ''}`}>
            <Pie
              options={getChartOptions(
                selectedLevel1 ? `선택됨: ${selectedLevel1}` : "Main Categories",
                currentLevel === 1 ? handleLevel1Click : undefined
              )}
              data={generateChartData(level1Data)}
            />
          </div>
        </div>

        {/* Level 2 Chart */}
        {currentLevel >= 2 && selectedLevel1 && level2Data.size > 0 ? (
          <div className={`chart-level chart-level-2 ${currentLevel === 2 ? '' : 'shifted'}`}>
            <h3>{selectedLevel1} - 세부 분류</h3>
            <div className={`chart-canvas ${currentLevel === 2 ? 'clickable' : ''}`}>
              {currentLevel === 2 && (
                <div className="click-hint">더 자세히 보기 👆</div>
              )}
              <Pie
                options={getChartOptions(
                  selectedLevel2 ? `선택됨: ${selectedLevel2}` : "Sub Categories",
                  currentLevel === 2 ? handleLevel2Click : undefined
                )}
                data={generateChartData(level2Data)}
              />
            </div>
          </div>
        ) : currentLevel === 1 ? (
          <GhostChart level={2} title="세부 분류" />
        ) : null}

        {/* Level 3 Chart */}
        {currentLevel >= 3 && selectedLevel1 && selectedLevel2 && level3Data.size > 0 ? (
          <div className="chart-level">
            <h3>{selectedLevel1} {'>'} {selectedLevel2}</h3>
            <div className="chart-canvas">
              <Pie
                options={getChartOptions("Detailed Categories")}
                data={generateChartData(level3Data)}
              />
            </div>
          </div>
        ) : currentLevel <= 2 ? (
          <GhostChart level={3} title="상세 분류" />
        ) : null}
      </div>
      
      {/* Navigation Breadcrumb */}
      <div className="chart-navigation">
        {/* Breadcrumb Path */}
        {/* <div className="chart-breadcrumb"> */}
          {/* <span className={`breadcrumb-item ${currentLevel === 1 ? 'active' : 'inactive'}`}>
            분류를 클릭해 보세요.
          </span>
           */}
          {/* {selectedLevel1 && (
            <>
              <span className="breadcrumb-arrow">→</span>
              <span className={`breadcrumb-item ${currentLevel === 2 ? 'active' : 'inactive'}`}>
                {selectedLevel1}
              </span>
            </>
          )}
          
          {selectedLevel2 && (
            <>
              <span className="breadcrumb-arrow">→</span>
              <span className="breadcrumb-item active">
                {selectedLevel2}
              </span>
            </>
          )} */}
        {/* </div> */}

        {/* Reset Button */}
        {(selectedLevel1 || selectedLevel2) && (
          <button className="chart-reset-btn" onClick={resetToLevel1}>
            🔄 처음부터 다시 보기
          </button>
        )}
      </div>
      
      {/* Gemini AI Analysis Section */}
      {isPopup && (
        <div className="gemini-section">
          <div className="gemini-header">
            <div>
              <h3 className="gemini-title">🤖 Gemini AI 데이터 분석</h3>
              <p className="gemini-subtitle">Google Gemini Flash 2.0으로 차트 데이터를 심층 분석합니다</p>
            </div>
          </div>
          
          <div className="data-summary">
            <h5>📊 분석 대상 데이터</h5>
            <p>• 전체 도서: {data.length}권</p>
            <p>• 주요 분류: {hierarchicalData.level1Map.size}개</p>
            <p>• 세부 분류: {level2Data.size > 0 ? level2Data.size : hierarchicalData.level2Map.size}개</p>
            <p>• 상세 분류: {level3Data.size > 0 ? level3Data.size : hierarchicalData.level3Map.size}개</p>
            {selectedLevel1 && <p>• 선택된 분야: {selectedLevel1}{selectedLevel2 ? ` > ${selectedLevel2}` : ''}</p>}
            {cacheInfo && (
              <p>• 캐시 상태: {cacheInfo.size}개 결과 저장됨 
                {/* {cacheInfo.size > 0 && (
                  <button 
                    onClick={handleClearCache}
                    style={{
                      marginLeft: '0.5rem',
                      padding: '0.2rem 0.5rem',
                      fontSize: '0.7rem',
                      backgroundColor: '#e74c3c',
                      color: 'white',
                      border: 'none',
                      borderRadius: '3px',
                      cursor: 'pointer'
                    }}
                  >
                    지우기
                  </button>
                )} */}
              </p>
            )}
          </div>
          
          <button 
            className="gemini-analyze-btn" 
            onClick={handleGeminiAnalysis}
            disabled={isAnalyzing}
          >
            {isAnalyzing ? (
              <>
                <div className="gemini-spinner"></div>
                {isStreaming ? 'AI 분석 스트리밍 중...' : 'AI 분석 중...'}
              </>
            ) : (
              <>
                ✨ Gemini로 데이터 분석하기
              </>
            )}
          </button>
          
          {analysisError && (
            <div className="gemini-error">
              ❌ {analysisError}
            </div>
          )}
          
          {geminiAnalysis && (
            <div className="gemini-result" ref={analysisRef}>
              <h4>📈 AI 분석 결과 {isStreaming && <span className="gemini-streaming-indicator">✨</span>}</h4>
              <ReactMarkdown
                rehypePlugins={[rehypeHighlight]}
                components={{
                  h2: ({children}) => <h2 style={{color: '#4285f4', marginTop: '1.5rem'}}>{children}</h2>,
                  h3: ({children}) => <h3 style={{color: '#34a853', marginTop: '1rem'}}>{children}</h3>,
                  strong: ({children}) => <strong style={{color: '#ea4335'}}>{children}</strong>,
                  p: ({children}) => <p style={{marginBottom: '0.8rem'}}>{children}</p>,
                  ul: ({children}) => <ul style={{marginLeft: '1rem', marginBottom: '0.8rem'}}>{children}</ul>,
                  li: ({children}) => <li style={{marginBottom: '0.3rem'}}>{children}</li>
                }}
              >
                {geminiAnalysis}
              </ReactMarkdown>
              {isStreaming && (
                <div className="gemini-streaming-cursor">|</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (isPopup) {
    return (
      <div className="pie-chart-modal" onClick={onClose}>
        <div className="pie-chart-modal-content" onClick={(e) => e.stopPropagation()}>
          <button className="pie-chart-close-btn" onClick={onClose}>
            ×
          </button>
          <h2 className="pie-chart-title">
            📊 도서 분류 상세 분석
          </h2>
          {chartContent}
        </div>
      </div>
    );
  }

  return chartContent;
};

export const PieChartButton: React.FC<PieChartButtonProps> = ({ data }) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const openPopup = () => setIsPopupOpen(true);
  const closePopup = () => setIsPopupOpen(false);

  return (
    <>
      <button className="pie-chart-button" onClick={openPopup}>
        📊 도서 분류 분석 보기
      </button>
      
      {isPopupOpen && (
        <PieChart 
          data={data} 
          isPopup={true} 
          onClose={closePopup} 
        />
      )}
    </>
  );
};